import { http } from "./http";
import {
  referencesApi,
  type WorkspaceReference,
  type ResolvedReference,
} from "./references.api";
export type BoardCard = {
  id: string;
  reference: WorkspaceReference;
  x: number;
  y: number;
};
export type CardReference = ResolvedReference;
export type BoardScene = {
  schemaVersion: 1 | 2 | 3;
  attachments?: { id: string; x: number; y: number }[];
  cards?: BoardCard[];
  elements: Record<string, unknown>[];
  viewBackgroundColor: string;
};
export type BoardContent = {
  title: string;
  visibility: "SCHOOL" | "SELECTED_MEMBERS";
  memberIds: string[];
  scene: BoardScene;
};
export type Board = BoardContent & {
  id: string;
  version: number;
  canEdit: boolean;
  canManage: boolean;
  canHistory: boolean;
};
export type BoardVersion = {
  version: number;
  createdAt: string;
  restoredFrom: number | null;
};
const base = "/v1/workspace/boards";
export const boardsApi = {
  changes: async (id: string, since: number, signal: AbortSignal) =>
    (
      await http.get<{ version: number }>(base + "/" + id + "/changes", {
        params: { since },
        signal,
        timeout: 25000,
      })
    ).data,
  upload: async (id: string, file: File, requestId: string) => {
    const form = new FormData();
    form.append("file", file);
    form.append("requestId", requestId);
    return (
      await http.post<{ id: string }>(base + "/" + id + "/files", form, {
        timeout: 20000,
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  file: async (id: string, file: string) => {
    const r = await http.get<Blob>(base + "/" + id + "/files/" + file, {
      responseType: "blob",
    });
    return {
      blob: r.data,
      name: decodeURIComponent(r.headers["x-file-name"] ?? "fichier"),
    };
  },
  searchReferences: referencesApi.search,
  resolveReferences: referencesApi.resolve,
  list: async (page: number) =>
    (
      await http.get<{
        items: (Pick<Board, "id" | "title" | "visibility" | "version"> & { createdAt: string; updatedAt: string })[];
        hasMore: boolean;
      }>(base, { params: { page } })
    ).data,
  get: async (id: string) => (await http.get<Board>(base + "/" + id)).data,
  members: async () =>
    (
      await http.get<{ id: string; firstName: string; lastName: string }[]>(
        base + "/members",
      )
    ).data,
  create: async (data: BoardContent & { requestId: string }) =>
    (await http.post<{ id: string; version: number }>(base, data)).data,
  save: async (
    id: string,
    data: BoardContent & { version: number; requestId: string },
  ) =>
    (await http.patch<{ id: string; version: number }>(base + "/" + id, data))
      .data,
  history: async (id: string, page: number) =>
    (
      await http.get<{ items: BoardVersion[]; hasMore: boolean }>(
        base + "/" + id + "/versions",
        { params: { page } },
      )
    ).data,
  revision: async (id: string, version: number) =>
    (
      await http.get<Pick<Board, "title" | "scene" | "version">>(
        base + "/" + id + "/versions/" + version,
      )
    ).data,
  restore: async (
    id: string,
    data: { version: number; sourceVersion: number; requestId: string },
  ) =>
    (
      await http.post<{ id: string; version: number }>(
        base + "/" + id + "/restore",
        data,
      )
    ).data,
};
